import '../../../../shared/component/flow-item/view/flow-item.css';

import { IonIcon } from '@ionic/react';
import { enterOutline, exitOutline, home, stopwatch } from 'ionicons/icons';
import { ReactElement, ReactNode } from 'react';
import { FlowItemBase } from '../../../../shared/component/flow-item/view/flow-item-base';
import { FlowItemProps } from '../../../../shared/component/flow-item/view/flow-item.types';
import { moveItem } from '../../../../shared/component/icon/move-item';
import { reportProduction } from '../../../../shared/component/icon/report-production';
import { waitForOperator } from '../../../../shared/component/icon/wait-for-operator';
import {
  FlowItem,
  FlowItemParameter,
  FlowItemRef,
} from '../../../../shared/model/flow.types';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';
import { cuttingCutIcon } from '../icon/cutting-cut-icon';
import { cuttingLinearCutIcon } from '../icon/cutting-linear-cut-icon';
import { cuttingRadialCutIcon } from '../icon/cutting-radial-cut-icon';

const CuttingFlowItemComponent = (props: FlowItemProps): ReactElement => {
  const {
    actions,
    flowItem,
    isItemSelected,
    isSectionSelected,
    canAddItem,
    canReorderItem,
    highlight,
    onAddItemsBefore,
    onAddItemsAfter,
    onSelectItem,
    system,
    executionInfo,
    isStartItem,
    isInactive,
    onLongPress,
    label,
    presentItemQuickActions,
    addPrecedingStem,
  } = props;

  const icon = getFlowItemIconFromItem(flowItem);

  return (
    <FlowItemBase
      flowItem={flowItem}
      canAddItem={canAddItem}
      canReorderItem={canReorderItem}
      isItemSelected={isItemSelected}
      isSectionSelected={isSectionSelected}
      highlight={highlight}
      onAddItemsBefore={onAddItemsBefore}
      onAddItemsAfter={onAddItemsAfter}
      onSelectItem={onSelectItem}
      icon={icon}
      actions={actions}
      system={system}
      executionInfo={executionInfo}
      isStartItem={isStartItem}
      isInactive={isInactive}
      onLongPress={onLongPress}
      label={label}
      presentItemQuickActions={presentItemQuickActions}
      addPrecedingStem={addPrecedingStem}
    />
  );
};

const getFlowItemIconFromItem = (flowItem: FlowItem): ReactNode => {
  const cutType = flowItem.parameters.find(
    (p) => p.ref === BeaconCuttingData.FlowItemParameterRef.CUT_TYPE
  );
  const waitForType = getWaitForType(flowItem);
  return getFlowItemIcon(flowItem.ref, cutType?.value, waitForType?.value);
};

export const getWaitForType = (
  flowItem: FlowItem
): FlowItemParameter<BeaconCuttingData.WaitForInputType> | undefined => {
  return flowItem.parameters.find(
    (p) =>
      p.ref === BeaconCuttingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
  );
};

export const getFlowItemIcon = (
  flowItemRef: FlowItemRef,
  cutType?: BeaconCuttingData.CutTypeEnum,
  waitForType?: BeaconCuttingData.WaitForInputType
): ReactNode => {
  let icon: ReactNode = <div>?</div>;
  const classList: string[] = ['flow-item-icon', 'full-hw', 'center'];
  if (flowItemRef === BeaconCuttingData.FlowItemRef.CUT) {
    const cutClassList: string[] = [...classList, 'bg-danger'];

    let cutTypeIcon = cuttingCutIcon;
    if (cutType === BeaconCuttingData.CutTypeEnum.LINEAR) {
      cutTypeIcon = cuttingLinearCutIcon;
    } else if (cutType === BeaconCuttingData.CutTypeEnum.RADIAL) {
      cutTypeIcon = cuttingRadialCutIcon;
    } else if (cutType === BeaconCuttingData.CutTypeEnum.ROTARY) {
      // TODO: do we need another icon?
      cutTypeIcon = cuttingRadialCutIcon;
      cutClassList.push('ringed-icon');
    }

    icon = (
      <div className={cutClassList.join(' ')}>
        <IonIcon icon={cutTypeIcon} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconCuttingData.FlowItemRef.MOVE) {
    icon = <IonIcon icon={moveItem} size="large" />;
  } else if (flowItemRef === BeaconCuttingData.FlowItemRef.WAIT_FOR) {
    if (waitForType === BeaconCuttingData.WaitForInputType.TIME) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={stopwatch} size="large" />
        </div>
      );
    } else if (waitForType === BeaconCuttingData.WaitForInputType.SENSOR) {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={enterOutline} size="large" />
        </div>
      );
    } else {
      icon = (
        <div className={[...classList, 'bg-alt'].join(' ')}>
          <IonIcon icon={waitForOperator} size="large" />
        </div>
      );
    }
  } else if (
    flowItemRef === BeaconCuttingData.FlowItemRef.PRODUCTION_REPORTING
  ) {
    icon = (
      <div className={[...classList, 'bg-success'].join(' ')}>
        <IonIcon icon={reportProduction} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconCuttingData.FlowItemRef.MOVE_HOME) {
    icon = (
      <div className={classList.join(' ')}>
        <IonIcon icon={home} size="large" />
      </div>
    );
  } else if (flowItemRef === BeaconCuttingData.FlowItemRef.TRIGGER_OUTPUT) {
    icon = (
      <div className={[...classList, 'bg-alt'].join(' ')}>
        <IonIcon icon={exitOutline} size="large" />
      </div>
    );
  }

  return icon;
};

export { CuttingFlowItemComponent };
